import { useState } from 'react';
import { scrollToTop } from '../../../../hooks/misc/helpers';
import {
  ATTRIBUTE_GROUPS,
  ATTRIBUTE_OPTIONS_MAP,
} from '../characterConstants';
import { AttributeKey } from '../characterTypes';
import { useCharacter } from '../CharacterContext';

// Utility functions for randomization
const getRandomItem = <T>(items: T[]): T => {
  return items[Math.floor(Math.random() * items.length)];
};

const useCharacterCreationUtils = () => {
  const { updateCharacter } = useCharacter();
  const [currentStep, setCurrentStep] = useState(0);

  // Calculate progress percentage
  const totalGroups = ATTRIBUTE_GROUPS.length;
  const progress = Math.round(((currentStep + 1) / totalGroups) * 100);

  // Navigation functions
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToNextStep = () => {
    if (currentStep < totalGroups - 1) {
      setCurrentStep(currentStep + 1);
    }
    scrollToTop();
  };

  // Function to handle attribute changes
  const handleAttributeChange = (
    key: AttributeKey,
    value: string | string[],
  ) => {
    updateCharacter({
      [key]: value,
    });
  };

  // Function to randomize a single attribute
  const randomizeAttribute = (attributeKey: AttributeKey) => {
    const options =
      ATTRIBUTE_OPTIONS_MAP[attributeKey as keyof typeof ATTRIBUTE_OPTIONS_MAP];

    if (!options) return;

    if (attributeKey === 'clothing') {
      // For clothing (multi-select), randomly select 1-3 items
      const count = Math.floor(Math.random() * 3) + 1;
      const selectedOptions: string[] = [];

      for (let i = 0; i < count; i++) {
        const randomOption = getRandomItem(options);
        if (!selectedOptions.includes(randomOption.id)) {
          selectedOptions.push(randomOption.id);
        }
      }

      handleAttributeChange(attributeKey, selectedOptions);
    } else if (attributeKey === 'name') {
      // For name attribute, filter out the 'custom' option for randomization
      const validOptions = options.filter((option) => option.id !== 'custom');
      const randomOption = getRandomItem(validOptions);
      handleAttributeChange(attributeKey, randomOption.id);
    } else {
      // For other single-select attributes
      const randomOption = getRandomItem(options);
      handleAttributeChange(attributeKey, randomOption.id);
    }
  };

  return {
    currentStep,
    progress,
    totalGroups,
    handleAttributeChange,
    randomizeAttribute,
    goToPreviousStep,
    goToNextStep,
  };
};

export default useCharacterCreationUtils;
